import { StandardEvents } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/** Keeps the cart-page recommendation grid in sync with successful cart changes. */
class RealCartComplementaryProducts extends HTMLElement {
  /** @type {AbortController | undefined} */
  #lifecycle;

  /** @type {AbortController | undefined} */
  #render;

  connectedCallback() {
    this.#lifecycle?.abort();
    this.#lifecycle = new AbortController();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#lifecycle.signal,
    });
  }

  disconnectedCallback() {
    this.#lifecycle?.abort();
    this.#render?.abort();
  }

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleCartUpdate = async (event) => {
    const lifecycle = this.#lifecycle;
    const sectionId = this.dataset.sectionId;
    const sourceDialog = event.target instanceof Element ? event.target.closest('quick-add-dialog dialog') : null;
    if (!sectionId) return;

    try {
      const { detail } = await event.promise;
      if (detail?.didError || lifecycle?.signal.aborted || !this.isConnected) return;

      // A failed later mutation must not cancel an earlier successful refresh.
      this.#render?.abort();
      const render = new AbortController();
      this.#render = render;
      const html = await sectionRenderer.getSectionHTML(sectionId, false, new URL(window.location.href), render.signal);

      // Quick Add restores focus when its closing animation finishes. Keep its
      // trigger mounted until then, and let the drawer take focus if it opens.
      await this.#waitForDialog(sourceDialog, render.signal);
      if (render.signal.aborted || lifecycle?.signal.aborted || !this.isConnected) return;

      const activeElement = document.activeElement;
      const ownedFocus = activeElement instanceof HTMLElement && this.contains(activeElement);
      const focusedCard = ownedFocus ? activeElement.closest('product-card') : null;
      const focusedProductId = focusedCard?.getAttribute('data-product-id');
      const nextLinkId = focusedCard?.nextElementSibling?.querySelector('.real-complementary-products__card-link')?.id;

      await morphSection(sectionId, html, { injectStylesheet: true });

      // Restore runtime line-item properties absent from server-rendered forms.
      const estimatedShipDateComponents =
        /** @type {NodeListOf<HTMLElement & { refresh?: () => void }>} */ (
          this.querySelectorAll('estimated-ship-date')
        );
      for (const component of estimatedShipDateComponents) component.refresh?.();

      // Morphing can remove a card or reuse its button for the next product.
      if (
        ownedFocus &&
        (!activeElement.isConnected || focusedCard?.getAttribute('data-product-id') !== focusedProductId) &&
        (document.activeElement === document.body || document.activeElement === activeElement)
      ) {
        this.#restoreFocus(nextLinkId);
      }
    } catch (error) {
      // Preserve the current grid on failed cart requests or section refreshes.
      // The next successful cart change will retry with authoritative Liquid.
      if (error?.name !== 'AbortError') console.error('[Cart recommendations] Unable to refresh', error);
    }
  };

  /**
   * @param {HTMLDialogElement | null} dialog
   * @param {AbortSignal} signal
   */
  async #waitForDialog(dialog, signal) {
    if (!dialog?.open || signal.aborted) return;

    await new Promise((resolve) => {
      const finish = () => {
        dialog.removeEventListener('close', finish);
        signal.removeEventListener('abort', finish);
        resolve(undefined);
      };
      dialog.addEventListener('close', finish, { once: true });
      signal.addEventListener('abort', finish, { once: true });
    });
  }

  /** @param {string | undefined} nextLinkId */
  #restoreFocus(nextLinkId) {
    if (document.querySelector('dialog[open]')) return;

    const nextLink = nextLinkId ? document.getElementById(nextLinkId) : null;
    const target =
      (nextLink && this.contains(nextLink) ? nextLink : null) ||
      this.querySelector('.real-complementary-products__card-link') ||
      document.querySelector('.cart-page .cart__checkout-button') ||
      document.querySelector('.cart-page .cart-title h1');

    if (!(target instanceof HTMLElement)) return;
    if (target.matches('h1')) {
      target.setAttribute('tabindex', '-1');
      target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
    }
    target.focus({ preventScroll: true });
  }
}

if (!customElements.get('real-cart-complementary-products')) {
  customElements.define('real-cart-complementary-products', RealCartComplementaryProducts);
}
